import * as RadixPopover from '@radix-ui/react-popover';
import { combineRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, type RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { restrictedZIndexes } from '../../../utils';
import { useText } from '../../../utils/hooks/use_text';
import { HeadlessButton } from '../../button';
import { Icon } from '../../icon';
import { IconType } from '../../icon/icon_dictionary';
import { useCellulaProviderContext } from '../../provider';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { Label } from '../base/label.component';
import { theme as commonTheme } from '../common_input.theme';
import { useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { InputComboboxContext } from './_input_combobox.context';
import { InputComboboxContent } from './_input_combobox_content.component';
import { InputComboboxTrigger } from './_input_combobox_trigger.component';
import { useInputCombobox } from './_use_input_combobox.hook';
import { theme } from './input_combobox.theme';

export type InputComboboxOptionAvatar = {
	src?: string | null;
	name?: string;
	status?: 'online' | 'offline';
};
export type InputComboboxOption = {
	label: string;
	value: string;
	subLabel?: string;
	icon?: IconType;
	avatar?: InputComboboxOptionAvatar;
	disabled?: boolean;
};
export type InputComboboxSingleValue = string | null;
export type InputComboboxMultipleValue = string[];

type InputComboboxRootWithoutFormProps = {
	label: string;
	children: ReactNode;
	loading?: boolean;
	hideLabel?: boolean;
	description?: string;
	contentEmptyText?: string;
	placeholder?: string;
	helperText?: string;
	invalid?: boolean;
	invalidText?: string;
	id?: string;
	optional?: boolean;
	required?: boolean;
	readOnly?: boolean;
	disabled?: boolean;
	disallowDeselection?: boolean;
	onOpenChange?: (open: boolean) => void;
	modal?: boolean;
} & (
	| {
			multiple?: never | false;
			onChange?: (option: InputComboboxSingleValue) => void;
			value?: string | null;
			defaultValue?: string | null;
			onInputChange?: (query: string) => void; // We do only allow manual filtering for single select. Due to complex UI/UX issues. Refine this feature deeply before start developing.
	  }
	| {
			multiple: true;
			onChange?: (options: InputComboboxMultipleValue) => void;
			value?: string[];
			defaultValue?: string[];
			onInputChange?: never;
	  }
);

const InputComboboxRootWithoutForm = forwardRef(function InputComboboxRootWithoutForm(
	{
		label,
		hideLabel = false,
		children,
		onChange,
		onInputChange,
		loading: controlledLoading,
		description,
		helperText,
		invalid,
		invalidText,
		id,
		optional,
		required,
		readOnly,
		disabled,
		value: controlledValue,
		defaultValue,
		multiple,
		contentEmptyText,
		placeholder,
		disallowDeselection,
		onOpenChange,
		modal,
	}: InputComboboxRootWithoutFormProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const initialRender = useRef(true);
	const [options, setOptions] = useState<InputComboboxOption[]>([]);
	const [optionsToRender, setOptionsToRender] = useState<InputComboboxOption[]>([]);
	const [value, setValue] = useState<string[]>(function parseInitialValue() {
		if (controlledValue !== undefined) {
			if (multiple) {
				return [...controlledValue];
			}
			return controlledValue != null ? [controlledValue] : [];
		}

		if (defaultValue !== undefined) {
			if (multiple) {
				return [...defaultValue];
			}
			return defaultValue != null ? [defaultValue] : [];
		}

		return [];
	});

	const [filterValue, setFilterValue] = useState('');
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(controlledLoading ?? true);

	const { portalElement } = useCellulaProviderContext();
	const { labelProps, descriptionProps, triggerProps, helpTextProps } = useInputCombobox({
		label,
		description,
		helperText,
		invalid,
		invalidText,
		id,
		hideLabel,
		optional,
		required,
		readOnly,
		disabled,
		value,
		multiple,
	});

	function onValueChange(value: string[]) {
		if (controlledValue === undefined) {
			setValue(value);
		}

		const selectedOptions = options.filter((option) => value.includes(option.value));

		if (multiple) {
			onChange?.(selectedOptions.map((option) => option.value));
		} else {
			onChange?.(selectedOptions[0]?.value ?? null);
		}
	}

	function handleOpenChange(open: boolean): void {
		setOpen(open);
		onOpenChange?.(open);
	}

	function deselectOption(option: InputComboboxOption | string) {
		onValueChange([...value.filter((value) => value !== (typeof option === 'string' ? option : option.value))]);
	}

	function onOptionAdd(option: InputComboboxOption) {
		setOptions((prev) => [...prev, option]);
	}

	function onOptionRemove(option: InputComboboxOption) {
		setOptions((prev) => [...prev.filter((o) => o.value !== option.value)]);
	}

	function onSelectOption(option: InputComboboxOption) {
		if (option.disabled) {
			return;
		}

		if (value.includes(option.value)) {
			if (!disallowDeselection) {
				deselectOption(option);
			}
		} else {
			if (!multiple) {
				onValueChange([option.value]);
			} else {
				onValueChange([...value, option.value]);
			}
		}

		if (!multiple) {
			handleOpenChange(false);
		}
	}

	function onSelectAllOptions() {
		onValueChange(options.filter((option) => !option.disabled).map((option) => option.value));
	}

	function onDeselectAllOptions() {
		onValueChange([]);
	}

	function onFilter(query: string) {
		setFilterValue(query);

		// Do manual filter if onInputChange callback is given
		if (onInputChange != null) {
			onInputChange(query);

			return;
		}

		if (!query) {
			setOptionsToRender(options);
			return;
		}
		const filteredOptions = options.filter(
			(option) =>
				option.label.toLowerCase().includes(query.toLowerCase()) ||
				(option.subLabel && option.subLabel.toLowerCase().includes(query.toLowerCase())),
		);
		setOptionsToRender(filteredOptions);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function clearFilterOnClose() {
			if (!open) {
				setFilterValue('');
				setOptionsToRender(options);
			}
		},
		[open],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenControlledValueChange() {
			if (initialRender.current || controlledValue === undefined) return;

			function getNextValue(): string[] {
				if (Array.isArray(controlledValue)) {
					return [...controlledValue];
				}
				if (typeof controlledValue === 'string') {
					return [controlledValue];
				}
				return [];
			}
			setValue(getNextValue());
		},
		[controlledValue, options],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenOptionsChangeDeselectRemovedOptions() {
			if (initialRender.current) return;
			setOptionsToRender(options);

			value.forEach((value) => {
				if (!options.some((option) => option.value === value)) {
					deselectOption(value);
				}
			});

			if (!controlledLoading) {
				setLoading(false);
			}
		},
		[value, options],
	);

	useEffect(
		function whenControlledLoadingChanges() {
			if (controlledLoading !== undefined) {
				setLoading(controlledLoading);
			}
		},
		[controlledLoading],
	);

	useEffect(function whenMounted() {
		initialRender.current = false;
	}, []);

	return (
		<InputComboboxContext.Provider
			value={{
				open,
				multiple: multiple ?? false,
				loading,
				value,
				contentEmptyText,
				placeholder,
				options,
				optionsToRender,
				readOnly: readOnly ?? false,
				onOptionAdd,
				onOptionRemove,
				onSelectOption,
				onSelectAllOptions,
				onDeselectAllOptions,
				onInputChange,
				onFilter,
				filterValue,
			}}
		>
			<elements.container>
				{children}
				{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
				<elements.label {...labelProps} />
				<elements.description {...descriptionProps} />
				<RadixPopover.Root onOpenChange={handleOpenChange} open={open} modal={modal}>
					<RadixPopover.Trigger asChild>
						<elements.trigger {...triggerProps} ref={ref}>
							<InputComboboxTrigger />
							<elements.triggerIcon data-state={open ? 'open' : undefined}>
								<Icon type='chevronDown' />
							</elements.triggerIcon>
						</elements.trigger>
					</RadixPopover.Trigger>
					<RadixPopover.Portal container={portalElement}>
						<elements.popoverContent>
							<InputComboboxContent />
						</elements.popoverContent>
					</RadixPopover.Portal>
				</RadixPopover.Root>
				<HelpText {...helpTextProps} />
			</elements.container>
		</InputComboboxContext.Provider>
	);
});

export type InputComboboxRootProps = InputComboboxRootWithoutFormProps & {
	name?: string;
	options?: RegisterOptions;
};

export const InputComboboxRoot = forwardRef(function InputComboboxRoot(
	{ name, options, ...props }: InputComboboxRootProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const formContext = useFormContext();
	const staticTexts = useText();

	if (formContext == null || name == null) {
		return <InputComboboxRootWithoutForm {...props} ref={ref} />;
	}

	return (
		<Controller
			control={formContext.control.control}
			name={name}
			defaultValue={props.defaultValue ?? undefined}
			rules={{
				...options,
				onChange: (event) => {
					props.onChange?.(event.target.value);
				},
			}}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				function handleChange(item: string[] | string | null): void {
					field.onChange(item);
				}

				return (
					<InputComboboxRootWithoutForm
						{...props}
						{...field}
						optional={inputPropsHelper.isOptional()}
						required={inputPropsHelper.isRequired()}
						readOnly={inputPropsHelper.isReadOnly()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						onChange={handleChange}
						defaultValue={undefined}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		></Controller>
	);
});

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
	`,
	label: styled(Label)`
		margin-bottom: ${commonTheme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	trigger: styled(HeadlessButton)`
		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		display: inline-flex;
		align-items: center;
		padding: ${commonTheme.getValue('basePaddingVertical')} ${commonTheme.getValue('basePaddingHorizontal')};
		background-color: ${commonTheme.getValue('baseBackground')};
		font: ${commonTheme.getValue('baseFont')};
		border: 1px solid ${commonTheme.getValue('baseBorderColor')};
		border-radius: ${commonTheme.getValue('baseBorderRadius')};
		color: ${theme.getValue('triggerFontColor')};
		transition: border-color 150ms;
		justify-content: space-between;
		cursor: default;

		&:focus-visible,
		&[aria-expanded='true'] {
			border-color: ${commonTheme.getValue('hoverBorderColor')};
			outline: 3px solid ${commonTheme.getValue('focusRingColor')};
		}

		&[data-selected='true'] {
			font: ${theme.getValue('triggerSelectedFont')};
			background: ${theme.getValue('triggerSelectedBackground')};
		}

		&[aria-invalid='true'] {
			border-color: ${commonTheme.getValue('invalidBorderColor')};
		}

		&[aria-disabled='true'] {
			cursor: not-allowed;
		}

		&[aria-readonly='true'],
		&[aria-disabled='true'] {
			opacity: 0.7;
			background: ${commonTheme.getValue('readOnlyBackground')};
			border-color: ${commonTheme.getValue('readOnlyBorderColor')};
		}

		@media (hover: hover) {
			&:hover:not([aria-disabled='true']) {
				border-color: ${commonTheme.getValue('hoverBorderColor')};
			}
		}
	`,
	triggerIcon: styled.span`
		flex: 0 0 auto;
		transition: transform 150ms;

		&[data-state='open'] {
			transform: rotate(180deg);
		}
	`,
	popoverContent: styled(RadixPopover.Content)`
		/* Needs to use a high z-index to have it appear on top of other elements */
		z-index: ${restrictedZIndexes.DANGER};
	`,
};
